import {
  proxyContextualizedProgressCircleFillStroke,
  proxyContextualizedProgressCircleFullStroke,
} from '@atlas-ui/utils';
import { css, keyframes } from '@emotion/react';
import type { CSSProperties, ComponentPropsWithoutRef } from 'react';
import { useProgressContext } from './context';

export type CircleProgressBarProps = ComponentPropsWithoutRef<'svg'>;

interface CircleProgressBarCSSProperties extends CSSProperties {
  '--atlas-ui-spinner-stroke-width': number;
  '--atlas-ui-spinner-size': string;
  '--atlas-ui-spinner-stroke-dasharray': number;
}

const svgStyles = css({
  width: 'var(--atlas-ui-spinner-size)',
  height: 'var(--atlas-ui-spinner-size)',
  transform: 'rotate(-90deg)',
});

const circleStyle = css({
  transformOrigin: '50% 50%',
  strokeWidth: 'var(--atlas-ui-spinner-stroke-width)',
});

const fillCircleStyle = css({
  strokeDasharray: 'var(--atlas-ui-spinner-stroke-dasharray)',
  transition: 'stroke-dashoffset 300ms ease-out',
});

const primaryFillStrokeStyle = css({
  stroke: proxyContextualizedProgressCircleFillStroke('--color-text-highlight'),
});

const primaryFullStrokeStyle = css({
  stroke: proxyContextualizedProgressCircleFullStroke(
    '--color-text-highlight-glassy'
  ),
});

const secondaryFillStrokeStyle = css({
  stroke: 'var(--color-text-xlow)',
});

const secondaryFullStrokeStyle = css({
  stroke: 'var(--color-text-xlow-glassy)',
});

const fullCircleStyles = {
  primary: primaryFullStrokeStyle,
  secondary: secondaryFullStrokeStyle,
  // TODO: This appearance is deprecated and should be removed in the future
  secondaryReverse: secondaryFullStrokeStyle,
};

const fillCircleStyles = {
  primary: primaryFillStrokeStyle,
  secondary: secondaryFillStrokeStyle,
  // TODO: This appearance is deprecated and should be removed in the future
  secondaryReverse: secondaryFillStrokeStyle,
};

const svgAnimation = keyframes({
  '0%': {
    transform: 'rotateZ(0deg)',
  },
  '100%': {
    transform: 'rotateZ(360deg)',
  },
});

const circleAnimation = keyframes({
  '0%, 25%': {
    strokeDashoffset: 250,
    transform: 'rotate(0deg)',
  },
  '50%, 75%': {
    strokeDashoffset: 50,
    transform: 'rotate(45deg)',
  },
  '100%': {
    strokeDashoffset: 250,
    transform: 'rotate(360deg)',
  },
});

const svgAnimationStyles = css({
  animation: `2s linear infinite ${svgAnimation}`,
});

const fillCircleAnimationStyles = css({
  animation: `2s ease-in-out infinite both ${circleAnimation}`,
});

const hideCircleStyles = css({
  display: 'none',
});

const sizeMap = {
  mini: 20,
  xsmall: 24,
  small: 32,
  medium: 64,
  large: 80,
};

const strokeWidthMap = {
  mini: 19,
  xsmall: 10,
  small: 10,
  medium: 10,
  large: 9,
};

const getDashArray = ({
  isMiniSize,
  isIndeterminate,
}: {
  isMiniSize: boolean;
  isIndeterminate: boolean;
}) => {
  if (isMiniSize) {
    return 240;
  }
  if (isIndeterminate) {
    return 300;
  }
  return 282.7;
};

export const CircleProgressBar = (props: CircleProgressBarProps) => {
  const { progressBarProps, size, isIndeterminate, percentage, appearance } =
    useProgressContext();
  const isMiniSize = size === 'mini';
  const center = 50;
  const r = isMiniSize ? 35 : 45;
  const c = 2 * r * Math.PI;
  const dashOffset = c - percentage * c;
  const dashArray = getDashArray({ isMiniSize, isIndeterminate });

  return (
    <svg
      {...progressBarProps}
      css={[svgStyles, isIndeterminate && svgAnimationStyles]}
      width={32}
      height={32}
      viewBox="0 0 100 100"
      fill="none"
      style={
        {
          '--atlas-ui-spinner-stroke-width': strokeWidthMap[size],
          '--atlas-ui-spinner-size': `${sizeMap[size]}px`,
          '--atlas-ui-spinner-stroke-dasharray': dashArray,
        } as CircleProgressBarCSSProperties
      }
      {...props}
    >
      <circle
        css={[
          circleStyle,
          fullCircleStyles[appearance],
          isMiniSize && hideCircleStyles,
        ]}
        cx={center}
        cy={center}
        r={r}
      />
      <circle
        css={[
          circleStyle,
          fillCircleStyle,
          fillCircleStyles[appearance],
          isIndeterminate && fillCircleAnimationStyles,
        ]}
        cx={center}
        cy={center}
        r={r}
        strokeDashoffset={dashOffset}
      />
    </svg>
  );
};
